import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { AlertCircle, CheckCircle2, Clock, User } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useProcessedOrders } from '../../hooks/useProcessedOrders';

export interface ProcessedOrdersModalRef {
  open: (receptionId: number) => void;
  close: () => void;
}

export const ProcessedOrdersModal = forwardRef<ProcessedOrdersModalRef>((_, ref) => {
  const modalRef = useRef<ModalCenterRef>(null);
  const [receptionId, setReceptionId] = useState<number | null>(null);

  const { data, isLoading } = useProcessedOrders(receptionId || 0, { per_page: 100 }, !!receptionId);

  useImperativeHandle(ref, () => ({
    open: (id: number) => {
      setReceptionId(id);
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
      setReceptionId(null);
    },
  }));

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const isoDateString = dateString.replace(' ', 'T');
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const orders = data?.data || [];

  return (
    <ModalCenter ref={modalRef} title="Pedidos Procesados" type="info" width="xxl">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No hay pedidos procesados</p>
          </div>
        ) : (
          <>
            {/* Encabezados */}
            <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-muted/70 rounded-lg text-sm text-foreground">
              <div className="col-span-2">Referencia</div>
              <div className="col-span-3">Tienda / Equipo</div>
              <div className="col-span-3">Procesado por</div>
              <div className="col-span-3">Fecha</div>
              <div className="col-span-1 text-center">Estado</div>
            </div>

            {/* Lista */}
            <ScrollArea className="h-[500px]">
              <div className="space-y-2 pr-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="grid grid-cols-12 gap-3 px-4 py-4 border border-border rounded-lg hover:border-primary hover:bg-muted/30 transition-colors bg-background"
                  >
                    {/* Referencia */}
                    <div className="col-span-2 flex flex-col justify-center">
                      <span className="text-sm font-medium text-foreground">
                        {order.order?.ref || `#${order.order_id}`}
                      </span>
                    </div>

                    {/* Tienda / Equipo */}
                    <div className="col-span-3 flex flex-col justify-center pl-3">
                      <span className="text-sm font-medium text-foreground truncate">
                        {order.order?.shop?.name || '-'}
                      </span>
                      {order.order?.shop?.team && (
                        <span className="text-xs text-muted-foreground truncate">{order.order.shop.team.name}</span>
                      )}
                    </div>

                    {/* Procesado por */}
                    <div className="col-span-3 flex items-center pl-3">
                      {order.processed_by_user ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm text-foreground truncate">{order.processed_by_user.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </div>

                    {/* Fecha */}
                    <div className="col-span-3 flex flex-col justify-center pl-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{formatDate(order.processed_at)}</span>
                      </div>
                      {order.incidence_type && (
                        <span className="text-xs font-medium text-red-600 dark:text-red-400 mt-1">
                          {order.incidence_type}
                        </span>
                      )}
                    </div>

                    {/* Estado */}
                    <div className="col-span-1 flex items-center justify-center">
                      {order.has_courier_incidence ? (
                        <Badge variant="destructive" className="text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Inc.
                        </Badge>
                      ) : (
                        <Badge
                          variant="default"
                          className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800"
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          OK
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        )}
      </div>
    </ModalCenter>
  );
});

ProcessedOrdersModal.displayName = 'ProcessedOrdersModal';
